<template>
  <Reader v-if="pubInfo" v-bind="readerProps" />
</template>

<script>
import api from '../services/api'
import * as css from '../services/css'
import Reader from './Reader.vue'

export default {
  name: 'Loader',

  inheritAttrs: false,

  props: {
    tizraId: {
      type: String,
      required: true,
    },
    pageNum: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      changedPage: false,
      pagingMode: null,
      pubInfo: null,
      logicalPages: null,
    }
  },

  computed: {
    documentTitle() {
      return this.pubInfo && this.pubInfo.title
        ? `${this.pubInfo.title} - ${this.pageNum}`
        : 'Tizra Reader'
    },

    readerProps() {
      const {logicalPages, pageNum, pagingMode, pubInfo, setPageNum} = this
      return {
        ...this.$attrs,
        logicalPages,
        pageNum,
        pagingMode,
        pubInfo,
        setPageNum,
      }
    },
  },

  watch: {
    documentTitle(v) {
      document.title = v
    },
  },

  created() {
    if (!this.tizraId) {
      this.$pub('fatal', 'Missing tizra-id')
      return
    }

    // Set the browser tab title the first time. This is also in watch,
    // because this.documentTitle changes when we get pubInfo.
    document.title = this.documentTitle

    // Handle access denied by exiting the reader to the
    // site-hosted access-denied page. Do this as a replacement, not
    // an assignment, so that browser back will get to the page
    // prior to the one that triggered access denied.
    this.$sub('denied', ({e} = {}) => {
      if (!this.changedPage) {
        // We haven't switched from the first page, suggesting an
        // error when first loading (access denied or otherwise).
        // Require manual input before trying again.
        this.$pub('fatal', 'Failed to load page')
        // If we have an Error, then also send to Rollbar.
        if (e) {
          this.$pub('error', e)
        }
      } else if (process.env.NODE_ENV === 'development') {
        // Site-hosted access-denied doesn't work on development,
        // because the Tizra server isn't rendering the page.
        this.$pub('fatal', 'Access denied on dev')
      } else {
        window.location.replace(`/${this.tizraId}/${this.pageNum}`)
      }
    })

    Promise.all([
      api
        .getPubInfo(this.tizraId)
        .then(pubInfo =>
          api
            .getPageInfo(this.tizraId, pubInfo.firstPage)
            .then(pageInfo => [pubInfo, pageInfo]),
        ),
      api.getLogicalPages(this.tizraId).catch(() => null),
    ])
      .then(([[pubInfo, pageInfo], logicalPages]) => {
        // The logical-pages response is not reliable. Sometimes it has extra
        // entries, and sometimes it doesn't have enough. Assume
        // pubInfo.numPages is authoritative and fill in logical-pages as
        // necessary.
        // https://github.com/Tizra/cubchicken/issues/560
        logicalPages = logicalPages || []
        for (let i = 0; i < pubInfo.numPages; i++) {
          if (logicalPages[i]) {
            continue
          }
          // Since we're mutating the logicalPages array in place, we can check
          // for an integer prior entry and increment it.
          if (logicalPages[i - 1]) {
            const lpNum = parseInt(logicalPages[i - 1])
            if (`${lpNum}` === logicalPages[i - 1]) {
              logicalPages[i] = `${lpNum + 1}`
            }
          }
          logicalPages[i] = `${pubInfo.firstPage + i}`
        }
        this.logicalPages = logicalPages

        this.pagingMode = pageInfo.imageUrl ? 'strip' : 'tower'

        // Setting this.pubInfo triggers rendering the Reader. But if
        // we're dealing with an XML doc, then load its CSS first, to
        // avoid FOUC.
        if (pageInfo.xmlUrl) {
          return window
            .fetch(`/${this.tizraId}/~~FreeAttachments/xhtml.css`)
            .then(r => {
              if (r.ok) {
                return r
                  .text()
                  .then(css.process)
                  .then(xmlCss => {
                    const style = document.createElement('style')
                    style.innerHTML = xmlCss
                    document.head.appendChild(style)
                  })
              }
            })
            .catch(e => {
              // Failure to process CSS, not just a 404 response.
              this.$pub('error', e)
            })
            .then(() => {
              this.pubInfo = pubInfo
            })
        } else {
          this.pubInfo = pubInfo
        }
      })
      .catch(e => {
        // Should only be here if getPubInfo failed, because we
        // already handled a failed getLogicalPages.
        this.$pub('fatal', e)
      })
  },

  methods: {
    setPageNum(p, {method = 'push', ...options} = {}) {
      this.changedPage = true
      this.$router[method](this.linkToPage(p, options))
    },
  },

  components: {Reader},

  inject: ['linkToPage'],
}
</script>
